import { createAnimationLegacy } from '../common/schemas/legacy/animation';
import { createExtendedPositionLegacy } from '../common/schemas/legacy/extended-position';
import { createWidgetPositionLegacy } from '../common/schemas/legacy/widget-position';

import type {
    EduLoginButtonEntity,
    EduLoginButtonItem,
    LegacyEduLoginButton,
    LegacyEduLoginButtonDefault,
    LegacyEduLoginButtonZero,
} from './schemas';
import { createEduLoginButtonItem } from './schemas';

/**
 * Which legacy placement the factory should produce — corresponds to the
 * envelope's `type` discriminator. Mirrors the button widget's variant contract:
 *  - `'default'` (or omitted): a button dropped into a normal column.
 *  - `'zero'`: a button dropped into a zero block (extended-position grid,
 *    fixed-width seeded button, `isSynchronizeStyle: false`).
 */
export type LegacyEduLoginButtonVariant = 'default' | 'zero';

/**
 * Per-variant creation knobs. The host app passes `buttonOverrides` to seed the
 * first button with localised text (`label`), a generated `elementId` (cuid2)
 * and the default icon SVG — values this package deliberately doesn't own.
 */
export type CreateLegacyEduLoginButtonOptions = {
    buttonOverrides?: Partial<EduLoginButtonItem>;
};

const createDefaultData = (options?: CreateLegacyEduLoginButtonOptions): LegacyEduLoginButtonDefault['data'] => ({
    buttons: [createEduLoginButtonItem(options?.buttonOverrides)],
    horizontalIndent: 'M',
    horizontalAlign: 'L',
    mobileHorizontalAlign: 'L',
    tabletHorizontalAlign: 'L',
    isSynchronizeStyle: true,
    hideOnDesktop: false,
    hideOnTablet: false,
    hideOnMobile: false,
    position: createWidgetPositionLegacy(),
    animation: createAnimationLegacy(),
});

/**
 * Default zero-block dimensions for a single button — mirrors the button
 * widget's zero factory (82×51 on every device).
 */
const createZeroData = (options?: CreateLegacyEduLoginButtonOptions): LegacyEduLoginButtonZero['data'] => ({
    buttons: [createEduLoginButtonItem({ widthType: 'fixed', ...options?.buttonOverrides })],
    horizontalIndent: 'M',
    horizontalAlign: 'L',
    mobileHorizontalAlign: 'L',
    tabletHorizontalAlign: 'L',
    isSynchronizeStyle: false,
    hideOnDesktop: false,
    hideOnTablet: false,
    hideOnMobile: false,
    position: createExtendedPositionLegacy({
        width: 82,
        tabletWidth: 82,
        mobileWidth: 82,
        height: 51,
        tabletHeight: 51,
        mobileHeight: 51,
    }),
    animation: createAnimationLegacy(),
});

/**
 * Factory for the current legacy EDU-login button entity — matches the persisted
 * widget JSON shape: `{ id, name: 'edu-login-button', columnId, data, … }`.
 */
export function createLegacyEduLoginButton(
    variant?: 'default',
    options?: CreateLegacyEduLoginButtonOptions
): LegacyEduLoginButtonDefault;
export function createLegacyEduLoginButton(
    variant: 'zero',
    options?: CreateLegacyEduLoginButtonOptions
): LegacyEduLoginButtonZero;
export function createLegacyEduLoginButton(
    variant: LegacyEduLoginButtonVariant = 'default',
    options?: CreateLegacyEduLoginButtonOptions
): LegacyEduLoginButton {
    if (variant === 'zero') {
        return {
            id: '',
            name: 'edu-login-button',
            columnId: '',
            type: 'zero',
            data: createZeroData(options),
        };
    }

    return {
        id: '',
        name: 'edu-login-button',
        columnId: '',
        data: createDefaultData(options),
    };
}

/**
 * Convenience entry-point that returns whichever shape is the current default
 * for new EDU-login button widgets. Delegates to {@link createLegacyEduLoginButton};
 * pass the variant to opt into the zero-block tag.
 */
export function createEduLoginButton(
    variant?: 'default',
    options?: CreateLegacyEduLoginButtonOptions
): LegacyEduLoginButtonDefault;
export function createEduLoginButton(
    variant: 'zero',
    options?: CreateLegacyEduLoginButtonOptions
): LegacyEduLoginButtonZero;
export function createEduLoginButton(
    variant?: LegacyEduLoginButtonVariant,
    options?: CreateLegacyEduLoginButtonOptions
): EduLoginButtonEntity {
    return createLegacyEduLoginButton(variant as any, options);
}
